import React, { Component } from "react";
import isEqual from "lodash.isequal";
import memoize from "memoize-one";

import { FilteredList, DevicesFilter, DevicesPickerTable } from "../../../../../components";
import { getDevicesAvailableToUser, reassignUsersDevices } from "../../../../../api";
import Auth from "../../../../../Auth";

const sortDevices = data => {
  return data.sort((a, b) => {
    if (a.group && !b.group) return 1;
    if (!a.group && b.group) return -1;
    if (a.group && a.group.id !== b.group.id) return a.group.name.localeCompare(b.group.name, "pl", { numeric: true });

    return (a.description || "").localeCompare(b.description || "", "pl", { numeric: true }); // sort by device description ASC
  });
};

const groupDevices = (data, filterValue) => {
  if (!data) {
    return [];
  }

  if (filterValue) { // don't return groups when filtering
    return data;
  }

  return data.reduce((a, x, i, arr) => {
    if (i === 0) {
      if (!x.group) {
        a.push({ id: 0, name: "Brak grupy", isGroup: true });
      }
      else {
        a.push({ ...x.group, isGroup: true });
      }
    }
    else if (arr[i - 1].group?.id !== x.group?.id) { // switched to different group
      a.push({ ...x.group, isGroup: true });
    }
    a.push(x);

    return a;
  }, []);
};

const getChangedDevices = (devicesGroups, selectedDevices) => {
  return devicesGroups.reduce((a, x) => {
    if (x.isGroup) {
      return a;
    }

    const selected = !!selectedDevices[x.id];
    if (selected && !x.selected) {
      a.selected.push(x);
    }
    else if (!selected && x.selected) {
      a.deselected.push(x);
    }

    return a;
  }, { selected: [], deselected: [] });
};

class UserDevices extends Component {
  onListChangedCallback = (filteredList, init, fullList) => {
    if (init) {
      const selectedElements = fullList.reduce((a, x) => {
        if (x.selected) {
          a.push(x.id);
        }

        return a;
      }, []);
  
      if (selectedElements.length) {
        this.selectElements(selectedElements, true, true);
      }
    }
  };

  memoizeGroupedDevices = memoize((devices, filterValue) => {
    return groupDevices(devices, filterValue);
  }, isEqual);

  filterDevices = (data, filters) => {
    if (!filters) {
      return data;
    }
  
    const { option, filterValue } = filters;
  
    return data.filter(x => {
      if (filterValue) {
        if (x.isGroup) {
          return false; // don't take groups into account
        }
        
        const strFilter = filterValue.toLowerCase();
        const isMatch = [x.description, x.make, x.model, x.vrn, x.vehicleNumber, x.serialNumber, x.imei, x.ccid, x.simCardPhoneNumber, x.id + ""]
          .filter(field => field?.toLowerCase()?.includes(strFilter))
          .length > 0;
    
        if (!isMatch) {
          return false;
        }
      }

      switch (option) {
        case "2":
          return x.selected || x.clientId === this.props.user.client?.id;
        case "3":
        default:
          return true;
      }
    });
  };

  save = (selected, deselected) => () => {
    return reassignUsersDevices({
      userId: this.props.user.id,
      addIds: selected.map(x => x.id),
      removeIds: deselected.map(x => x.id)
    });
  };

  render() {
    const { user } = this.props;
    const userId = user.id;
    const _this = this;

    return (
      <Auth.Consumer>
        {({ hasPermission }) => (
          <FilteredList multiselect dependencyId={userId} endpoint={getDevicesAvailableToUser} data={{ userId }} keyField="id" listFilter={this.filterDevices} listSorter={sortDevices} listChangedCallback={this.onListChangedCallback}>
            {({ fullList, filteredList, selectedElements, isFetching, filterPhrase: { option, filterValue } }, { refresh, refreshComponents, onFilterChanged, selectElement, selectAll, deselectAll, areAllSelected, selectedCount, selectElements }) => {
              _this.selectElements = selectElements;
              const devicesGroups = this.memoizeGroupedDevices(filteredList, filterValue);
              const { selected, deselected } = getChangedDevices(fullList, selectedElements);
              const isDirty = !!(selected.length || deselected.length);
              const canAssignDevicesToUsers = hasPermission("assigningDevicesToUsers");
              const canAccessDeveloperSettings = hasPermission("accessToDeveloperSettings");
              const canAccessSimCards = hasPermission("managingSimCards");

              return (
                <div style={{ overflowY: "hidden", margin: "-1.25rem", height: "calc(100% - 1.5rem)" }}>
                  <div style={{ margin: "1.25rem", height: "calc(100% - 2.5rem)" }}>
                    <DevicesFilter
                      defaultValue={{ option: "2", filterValue: "" }}
                      assignedEntries
                      allEntries
                      filterField
                      option={option}
                      onChange={onFilterChanged}
                      canAssignDevices={canAssignDevicesToUsers}
                      kind={2}
                      userId={userId}
                    />

                    <DevicesPickerTable
                      isFetching={isFetching} devices={fullList} devicesGroups={devicesGroups} refreshDevicesList={refresh}
                      selectedDevices={selectedElements} selectElement={selectElement} selectElements={selectElements} selected={selected}
                      deselected={deselected} isDirty={isDirty} canAssignDevices={canAssignDevicesToUsers}
                      canAccessDeveloperSettings={canAccessDeveloperSettings}  context={`${userId}_${option}`} filterValue={filterValue}
                      save={this.save(selected, deselected)} canAccessSimCards={canAccessSimCards}
                    />
                  </div>
                </div>
              );
            }}
          </FilteredList>
        )}
      </Auth.Consumer>
    );
  }
}

export default UserDevices;
